import { Tokens } from "./auth";

const STORAGE_KEY = "iec62443_tokens";

export type SessionTokens = Tokens & {
  expires_at: number;
};

function decodeJwtExpiresAt(accessToken: string): number | null {
  const parts = accessToken.split(".");
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (payload && typeof payload.exp === "number") {
      return payload.exp * 1000;
    }
  } catch {
    // ignore malformed tokens
  }
  return null;
}

export function decodeJwtIssuer(accessToken: string): string | null {
  const parts = accessToken.split(".");
  if (parts.length !== 3) return null;
  try {
    const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (payload && typeof payload.iss === "string") {
      return payload.iss;
    }
  } catch {
    // ignore malformed tokens
  }
  return null;
}

export function withComputedExpiry(tokens: Tokens): SessionTokens {
  const jwtExpiry = decodeJwtExpiresAt(tokens.access_token);
  const fallbackExpiry = Date.now() + Math.max(30, tokens.expires_in ?? 0) * 1000;
  const expires_at = jwtExpiry ? Math.min(jwtExpiry, fallbackExpiry) : fallbackExpiry;
  return { ...tokens, expires_at };
}

export function loadStoredTokens(): SessionTokens | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY) ?? sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SessionTokens;
    if (!parsed || typeof parsed.access_token !== "string" || typeof parsed.refresh_token !== "string") {
      return null;
    }
    if (typeof parsed.expires_at !== "number") {
      const hydrated = withComputedExpiry(parsed);
      persistTokens(hydrated);
      return hydrated;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function persistTokens(tokens: SessionTokens | null) {
  try {
    if (!tokens) {
      localStorage.removeItem(STORAGE_KEY);
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    const payload = JSON.stringify(tokens);
    try {
      localStorage.setItem(STORAGE_KEY, payload);
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      sessionStorage.setItem(STORAGE_KEY, payload);
    }
  } catch {
    // ignore storage failures (e.g., blocked storage); keep in-memory session
  }
}
